import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Search, Sparkles, X, Loader2 } from 'lucide-react'
import { useLocation } from '../contexts/LocationContext'
import { useFilters } from '../contexts/FilterContext'
import { getExampleQueries } from '../utils/mockQueryHandler'
import QueryResponse from './QueryResponse'

const SERVERLESS_BASE_API_URL =
  'https://ug-district-gen-ai-api.edwin-m.workers.dev/query'
// const SERVERLESS_BASE_API_URL = 'http://localhost:36429/query'

/**
 * QueryInterface Component
 *
 * Natural language query interface for the district dashboard.
 * Allows users to ask questions about facility data in plain language.
 *
 * This is a prototype using mock responses. Real AI integration will be
 * plugged in via API endpoints.
 */
export default function QueryInterface({
  isOpen,
  onClose,
  onApplyFilters,
  onSwitchView,
}) {
  const [query, setQuery] = useState('')
  const [response, setResponse] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [showExamples, setShowExamples] = useState(true)
  const inputRef = useRef(null)

  // Context hooks
  const {
    selectedDistrict,
    selectedSubcounty,
    selectedParish,
    selectedVillage,
    getLocationCodeById,
  } = useLocation()
  const { activeCategory } = useFilters()

  // Get example queries for current category
  const exampleQueries = getExampleQueries(activeCategory)

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Listen for suggestion clicks
  useEffect(() => {
    const handleSuggestionClick = event => {
      if (event.detail?.suggestion) {
        setQuery(event.detail.suggestion)
        setShowExamples(false)
      }
    }

    window.addEventListener('suggestion-click', handleSuggestionClick)
    return () =>
      window.removeEventListener('suggestion-click', handleSuggestionClick)
  }, [])

  useEffect(() => {
    if (!query) {
      setResponse(null)
      setShowExamples(true)
    } else if (!showExamples) {
      // Auto-submit after a brief delay for better UX
      setTimeout(() => {
        handleSubmit()
      }, 100)
    }
  }, [query, showExamples])

  const handleSubmit = async e => {
    e?.preventDefault()

    if (!query.trim() || isProcessing) return

    setIsProcessing(true)
    setShowExamples(false)

    try {
      // Build current location object with location codes
      const currentLocation = {
        district: getLocationCodeById(selectedDistrict),
        subcounty: getLocationCodeById(selectedSubcounty),
        parish: getLocationCodeById(selectedParish),
        village: getLocationCodeById(selectedVillage),
      }

      // Process query with mock handler
      // TODO: Replace with actual API call
      const apiResponse = await fetch(SERVERLESS_BASE_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          location: currentLocation,
          category: activeCategory,
        }),
      })
      const result = await apiResponse.json()

      setResponse(result)
    } catch (error) {
      setResponse({
        type: 'error',
        text: 'Sorry, something went wrong processing your query. Please try again.',
        data: {},
      })
      // eslint-disable-next-line no-console
      console.error('Query processing error:', error)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleExampleClick = example => {
    setQuery(example)
    setShowExamples(false)
  }

  const handleClear = () => {
    setQuery('')
    setResponse(null)
    setShowExamples(true)
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  const handleApplyFilters = filters => {
    if (onApplyFilters) {
      onApplyFilters(filters)
    }
    if (onClose) {
      onClose()
    }
  }

  const handleSwitchView = view => {
    if (onSwitchView) {
      onSwitchView(view)
    }
    if (onClose) {
      onClose()
    }
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[1000] flex items-start justify-center bg-black bg-opacity-50 !mt-0 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-8 md:my-16">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-semibold text-gray-800">
                Ask About Your Data
              </h2>
              <p className="text-sm text-gray-500">
                Natural language queries powered by AI
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Query Input */}
        <div className="p-4 md:p-6 space-y-4">
          <form onSubmit={handleSubmit} className="relative">
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask a question about facilities in your area..."
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-800 placeholder-gray-400"
                disabled={isProcessing}
              />
              <button
                type="submit"
                disabled={!query.trim() || isProcessing}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                aria-label="Submit query"
              >
                {isProcessing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Search className="w-5 h-5" />
                )}
              </button>
            </div>
          </form>

          {/* Processing Indicator */}
          {isProcessing && (
            <div className="flex items-center justify-center py-8">
              <div className="text-center space-y-3">
                <div className="inline-block p-3 bg-blue-50 rounded-full">
                  <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                </div>
                <p className="text-sm text-gray-600">
                  Processing your query...
                </p>
              </div>
            </div>
          )}

          {/* Example Queries */}
          {showExamples && !isProcessing && !response && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">
                Try these example queries:
              </p>
              <div className="space-y-2">
                {exampleQueries.map((example, index) => (
                  <button
                    key={index}
                    onClick={() => handleExampleClick(example)}
                    className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <Search className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                      <span className="text-sm text-gray-700 group-hover:text-gray-900">
                        {example}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Query Response */}
          {response && !isProcessing && (
            <div className="space-y-3">
              <QueryResponse
                response={response}
                onApplyFilters={handleApplyFilters}
                onSwitchView={handleSwitchView}
                onClear={handleClear}
              />
            </div>
          )}
        </div>

        {/* Footer Info */}
        <div className="p-4 md:p-6 border-t border-gray-100 bg-gray-50 rounded-b-xl">
          <div className="flex items-start gap-2 text-xs text-gray-500">
            <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>
              <span className="font-medium">AI-Powered Insights:</span>{' '}
              Responses are generated using AI and may occasionally contain
              inaccuracies. Always verify critical information with the
              underlying facility data.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

QueryInterface.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApplyFilters: PropTypes.func,
  onSwitchView: PropTypes.func,
}
